import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type StandingRow, type GroupStageGroup, type GroupStandingRow } from '../lib/api';
import { SideRounds } from '../components/BracketCanvas';
import { PageSpinner } from '../components/Spinner';
import { displayClanName } from '../lib/displayName';
import { Trophy, GitBranch } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Watch — the public spectator page. No login, no sidebar, no admin chrome:
// just the live bracket (elimination comps) or standings (leagues), wearing
// the org's accent. Lives OUTSIDE AuthGate; polls every 30s so a link pinned
// in Discord behaves like a live scoreboard.
// ─────────────────────────────────────────────────────────────────────────────

function PublicStandings({ standings, standingsType }: { standings: StandingRow[]; standingsType?: string }) {
  const isScore = standingsType === 'SCORE';
  if (standings.length === 0) {
    return <p className="text-sm text-gray-500">No confirmed matches yet — check back after match night.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-700/50 text-left text-xs uppercase tracking-wider text-gray-500">
            <th className="pb-3 pr-4">#</th>
            <th className="pb-3 pr-4">Team</th>
            <th className="pb-3 pr-4 text-center">MP</th>
            {isScore ? (
              <th className="pb-3 pr-4 text-center">Total Score</th>
            ) : (
              <>
                <th className="pb-3 pr-4 text-center">W</th>
                <th className="pb-3 pr-4 text-center">L</th>
              </>
            )}
            <th className="pb-3 text-center font-bold text-violet-400">PTS</th>
          </tr>
        </thead>
        <tbody>
          {standings.map((row, i) => (
            <tr key={row.name} className="border-b border-gray-700/30">
              <td className="py-3 pr-4 text-gray-500">{i + 1}</td>
              <td className="py-3 pr-4 font-medium text-white">{displayClanName(row.name.replace(/^CLAN#/, ''))}</td>
              <td className="py-3 pr-4 text-center text-gray-400">{row.matchesPlayed}</td>
              {isScore ? (
                <td className="py-3 pr-4 text-center text-gray-400">{(row.totalScore ?? 0).toLocaleString()}</td>
              ) : (
                <>
                  <td className="py-3 pr-4 text-center text-green-400">{row.wins}</td>
                  <td className="py-3 pr-4 text-center text-red-400">{row.losses}</td>
                </>
              )}
              <td className="py-3 text-center font-bold text-violet-300">{row.points.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// GROUP_STAGE only: per-group standings (sanitised — team names + records, no
// player data). Renders alongside the playoff bracket once it exists.
function PublicGroups({ groups }: { groups: GroupStageGroup[] }) {
  const letter = (g: number) => String.fromCharCode(65 + g);
  if (groups.length === 0) {
    return <p className="text-sm text-gray-500">Groups haven't been drawn yet — check back soon.</p>;
  }
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {[...groups].sort((a, b) => a.group - b.group).map((grp) => (
        <div key={grp.group} className="rounded-xl border border-gray-800 bg-surface-950 p-4">
          <h3 className="mb-3 font-display text-sm font-black uppercase tracking-wider text-white">Group {letter(grp.group)}</h3>
          <div className="overflow-x-auto">
            <table className="w-full whitespace-nowrap text-sm">
              <thead>
                <tr className="border-b border-gray-700/50 text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="pb-2 pr-3">#</th>
                  <th className="pb-2 pr-3">Team</th>
                  <th className="pb-2 pr-3 text-center">W-L</th>
                  <th className="pb-2 pr-3 text-center">Score</th>
                  <th className="pb-2 text-center">+/-</th>
                </tr>
              </thead>
              <tbody>
                {[...grp.standings].sort((a, b) => a.rank - b.rank).map((r: GroupStandingRow) => {
                  const diff = r.pointsFor - r.pointsAgainst;
                  return (
                    <tr key={r.id} className={r.advanced ? 'border-b border-gray-700/30 bg-green-500/[0.05]' : 'border-b border-gray-700/30'}>
                      <td className="py-2 pr-3 text-gray-500">{r.rank}</td>
                      <td className="py-2 pr-3 font-medium text-white">
                        {displayClanName(r.id.replace(/^CLAN#/, ''))}
                        {r.advanced && <span className="ml-2 rounded bg-green-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase text-green-400">Adv</span>}
                      </td>
                      <td className="py-2 pr-3 text-center text-gray-400">{r.wins}-{r.losses}</td>
                      <td className="py-2 pr-3 text-center font-bold text-violet-300">{r.score % 1 === 0 ? r.score : r.score.toFixed(1)}</td>
                      <td className="py-2 text-center text-gray-400">{diff > 0 ? `+${diff}` : diff}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

export function Watch() {
  const { compId } = useParams<{ compId: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ['publicComp', compId],
    queryFn: () => api.publicComp(compId!),
    enabled: !!compId,
    refetchInterval: 30_000,
    retry: false,
  });

  if (isLoading) return <div className="min-h-screen"><PageSpinner /></div>;

  if (error || !data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 px-4 text-center">
        <GitBranch className="h-12 w-12 text-gray-700" strokeWidth={1.5} />
        <h1 className="font-display text-xl font-black uppercase tracking-wider text-gray-300">
          Nothing to watch here
        </h1>
        <p className="max-w-sm text-sm text-gray-500">
          This tournament doesn't have a public page (or the link is wrong). Ask the organiser for a fresh link.
        </p>
      </div>
    );
  }

  const isElim = data.format === 'ELIMINATION_SINGLE' || data.format === 'ELIMINATION_DOUBLE';
  const isDouble = data.format === 'ELIMINATION_DOUBLE';
  const isGroupStage = data.format === 'GROUP_STAGE';
  const slots = data.slots ?? [];
  const accent = data.accentColor;

  return (
    <div className="min-h-screen text-gray-200">
      <div
        className="border-b border-gray-800"
        style={accent ? { borderTopWidth: 3, borderTopColor: accent, borderTopStyle: 'solid' } : undefined}
      >
        <div className="mx-auto flex max-w-6xl items-end justify-between gap-4 px-4 py-6">
          <div className="min-w-0">
            {data.orgName && (
              <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em]" style={accent ? { color: accent } : undefined}>
                {data.orgName}
              </p>
            )}
            <h1 className="mt-1 truncate font-display text-3xl font-black uppercase tracking-tight text-white sm:text-4xl">
              {data.name}
            </h1>
            <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
              {isDouble ? 'Double elimination' : isElim ? 'Single elimination' : isGroupStage ? 'Groups → Single elimination' : 'League'} · {data.status} · live view
            </p>
          </div>
          {data.champion && (
            <div className="flex shrink-0 items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2">
              <Trophy className="h-4 w-4 text-amber-400" />
              <span className="text-sm font-bold text-amber-300">{data.champion}</span>
            </div>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-8">
        {isGroupStage ? (
          <div className="space-y-10">
            <PublicGroups groups={data.groups ?? []} />
            {slots.length > 0 && (
              <div>
                <p className="mb-4 text-[10px] font-semibold uppercase tracking-wider text-gray-500">Playoffs</p>
                <SideRounds compId={data.compId} slots={slots} shots={[]} side="WINNERS" canEnter={false} />
              </div>
            )}
          </div>
        ) : isElim ? (
          slots.length === 0 ? (
            <p className="text-sm text-gray-500">The bracket hasn't been generated yet — check back soon.</p>
          ) : (
            <div className="space-y-8">
              {isDouble && (
                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Winners bracket</p>
              )}
              <SideRounds compId={data.compId} slots={slots} shots={[]} side="WINNERS" canEnter={false} />
              {isDouble && (
                <>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Losers bracket</p>
                  <SideRounds compId={data.compId} slots={slots} shots={[]} side="LOSERS" canEnter={false} />
                </>
              )}
              {slots.some(s => s.side === 'GRAND_FINAL') && (
                <SideRounds compId={data.compId} slots={slots} shots={[]} side="GRAND_FINAL" canEnter={false} />
              )}
            </div>
          )
        ) : (
          <PublicStandings standings={data.standings ?? []} standingsType={data.standingsType} />
        )}
      </div>

      <footer className="mx-auto max-w-6xl px-4 pb-8">
        <p className="text-[10px] uppercase tracking-wider text-gray-600">
          Powered by GG Anvil · updates every 30 seconds
        </p>
      </footer>
    </div>
  );
}
